import React, { useState, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Slider } from '@/components/ui/slider';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Presentation,
  Download,
  Play,
  ChevronLeft,
  ChevronRight,
  Plus,
  Edit,
  Trash2,
  Save,
  FileText,
  Maximize,
  Loader2,
  ArrowLeft,
  Palette,
  Wand2,
  Layout,
  Grid3X3,
  Library } from
'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { geminiService } from '@/services/geminiService';
import { motion, AnimatePresence } from 'motion/react';
import { useAuth } from '@/contexts/AuthContext';
import SlideRenderer, { Slide } from './SlideRenderer';
import PresentationView from './PresentationView';
import LayoutSelector from './LayoutSelector';
import PresentationManager from './PresentationManager';

// Define template types directly
interface Template {
  id: string;
  name: string;
  description: string;
  primaryColor: string;
  secondaryColor: string;
  bgColor: string;
  textColor: string;
  accentColor?: string;
  fontFamily?: string;
}

// Default templates
const templates: Template[] = [
{
  id: 'modern',
  name: 'Modern',
  description: 'Clean and contemporary design',
  primaryColor: '#2563eb',
  secondaryColor: '#3b82f6',
  bgColor: '#ffffff',
  textColor: '#1f2937',
  accentColor: '#60a5fa',
  fontFamily: 'Inter, sans-serif'
},
{
  id: 'professional',
  name: 'Professional',
  description: 'Corporate and business-focused',
  primaryColor: '#1f2937',
  secondaryColor: '#374151',
  bgColor: '#f9fafb',
  textColor: '#111827',
  accentColor: '#6b7280',
  fontFamily: 'Roboto, sans-serif'
},
{
  id: 'creative',
  name: 'Creative',
  description: 'Vibrant and artistic design',
  primaryColor: '#7c3aed',
  secondaryColor: '#8b5cf6',
  bgColor: '#faf5ff',
  textColor: '#581c87',
  accentColor: '#a78bfa',
  fontFamily: 'Poppins, sans-serif'
}];


interface PresentationData {
  id: string;
  title: string;
  description: string;
  subject: string;
  grade_level: string;
  slides_data: string; // JSON string of slides
  template_theme: string;
  total_slides: number;
  created_at: string;
  updated_at: string;
}

interface GenerationStep {
  id: 'template' | 'content' | 'review';
  title: string;
  description: string;
}

const steps: GenerationStep[] = [
{
  id: 'template',
  title: 'Choose Template',
  description: 'Select a design template for your presentation'
},
{
  id: 'content',
  title: 'Content Details',
  description: 'Provide topic and generation preferences'
},
{
  id: 'review',
  title: 'Review & Edit',
  description: 'Review and customize your generated slides'
}];


const TemplateSelector: React.FC<{
  onSelectTemplate: (template: Template) => void;
  selectedTemplate?: Template;
}> = ({ onSelectTemplate, selectedTemplate }) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="ldkxbj4aa" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">
      {templates.map((template) =>
      <Card
        key={template.id}
        className={`cursor-pointer transition-all duration-200 hover:shadow-lg ${
        selectedTemplate?.id === template.id ?
        'ring-2 ring-blue-500 shadow-lg' :
        'hover:ring-2 hover:ring-gray-300'}`
        }
        onClick={() => onSelectTemplate(template)} data-id="kbxhc3c0a" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">

          <CardContent className="p-4" data-id="ov7b35j8n" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">
            <div
            className="w-full h-32 rounded-lg mb-3 flex items-center justify-center text-white font-semibold"
            style={{
              background: `linear-gradient(135deg, ${template.primaryColor}, ${template.secondaryColor})`
            }} data-id="biq4az5sv" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">

              {template.name}
            </div>
            <h3 className="font-semibold mb-1" data-id="w5abfy9r1" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">{template.name}</h3>
            <p className="text-sm text-gray-600" data-id="dtlvc2ds6" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">{template.description}</p>
            <div className="flex gap-2 mt-3" data-id="h9yfbt564" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">
              <div
              className="w-4 h-4 rounded-full border"
              style={{ backgroundColor: template.primaryColor }}
              title="Primary Color" data-id="7rkjoz33h" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx" />

              <div
              className="w-4 h-4 rounded-full border"
              style={{ backgroundColor: template.secondaryColor }}
              title="Secondary Color" data-id="slh9bom3z" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx" />

              {template.accentColor &&
            <div
              className="w-4 h-4 rounded-full border"
              style={{ backgroundColor: template.accentColor }}
              title="Accent Color" data-id="n6y750gaz" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx" />

            }
            </div>
          </CardContent>
        </Card>
      )}
    </div>);

};

const EnhancedAISlidesGenerator: React.FC = () => {
  // UI State
  const [currentStep, setCurrentStep] = useState<GenerationStep['id']>('template');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isPresentationMode, setIsPresentationMode] = useState(false);
  const [isEditingSlide, setIsEditingSlide] = useState<string | null>(null);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [showLayoutSelector, setShowLayoutSelector] = useState(false);
  const [activeTab, setActiveTab] = useState('generator');

  // Data State
  const [presentations, setPresentations] = useState<PresentationData[]>([]);
  const [currentPresentation, setCurrentPresentation] = useState<{
    data: PresentationData;
    slides: Slide[];
    template: Template;
  } | null>(null);
  const [lessonPlans, setLessonPlans] = useState<any[]>([]);
  const [loadingLessonPlans, setLoadingLessonPlans] = useState(false);

  // Generation Form State
  const [selectedTemplate, setSelectedTemplate] = useState<Template | undefined>();
  const [topic, setTopic] = useState('');
  const [gradeLevel, setGradeLevel] = useState('');
  const [slideCount, setSlideCount] = useState([10]);
  const [subject, setSubject] = useState('');
  const [selectedLessonPlan, setSelectedLessonPlan] = useState('');
  const [additionalNotes, setAdditionalNotes] = useState('');

  const { toast } = useToast();
  const { user, hasAIAccess } = useAuth();

  React.useEffect(() => {
    const initializeComponent = async () => {
      try {
        if (user?.ID) {
          await loadLessonPlans();
          await loadSavedPresentations();
        }
      } catch (error) {
        console.error('Error initializing Enhanced AISlidesGenerator:', error);
        toast({
          title: "Initialization Error",
          description: "Failed to load component data. Please refresh the page.",
          variant: "destructive"
        });
      } finally {
        setIsLoading(false);
      }
    };

    if (user !== undefined) {
      initializeComponent();
    }
  }, [user]);

  const loadLessonPlans = async () => {
    if (!user?.ID) return;

    try {
      setLoadingLessonPlans(true);
      const { data, error } = await window.ezsite.apis.tablePage(15206, {
        PageNo: 1,
        PageSize: 50,
        OrderByField: 'id',
        IsAsc: false,
        Filters: [{
          name: 'teacher_id',
          op: 'Equal',
          value: user.ID
        }]
      });

      if (error) throw error;
      setLessonPlans(data?.List || []);
    } catch (error) {
      console.error('Error loading lesson plans:', error);
      toast({
        title: "Error",
        description: "Failed to load lesson plans. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoadingLessonPlans(false);
    }
  };

  const loadSavedPresentations = async () => {
    if (!user?.ID) return;

    try {
      const { data, error } = await window.ezsite.apis.tablePage(15255, {
        PageNo: 1,
        PageSize: 50,
        OrderByField: 'id',
        IsAsc: false,
        Filters: [{
          name: 'teacher_id',
          op: 'Equal',
          value: user.ID
        }]
      });

      if (error) throw error;
      setPresentations(data?.List || []);
    } catch (error) {
      console.error('Error loading presentations:', error);
      // Don't show error toast for this as it might be the first time
    }
  };

  const generateImageUrl = (keywords: string): string => {
    const cleanKeywords = keywords.
    toLowerCase().
    replace(/[^a-zA-Z0-9\s]/g, '').
    replace(/\s+/g, ',').
    slice(0, 100);
    return `https://source.unsplash.com/1200x800/?${cleanKeywords || 'education'}`;
  };

  const extractKeywords = (title: string, content: string): string => {
    const text = `${title} ${content}`.toLowerCase();
    const stopWords = ['the', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for', 'of', 'with', 'by', 'is', 'are', 'was', 'were', 'a', 'an'];

    const words = text.
    replace(/[^a-zA-Z0-9\s]/g, '').
    split(/\s+/).
    filter((word) => word.length > 3 && !stopWords.includes(word)).
    slice(0, 3);

    return words.join(',') || topic.toLowerCase();
  };

  // Enhanced content parsing and validation
  const parseAIResponse = (response: string, requestedSlideCount: number): Slide[] => {
    console.log('Parsing AI response:', response.substring(0, 200));

    // Try to parse as JSON first
    try {
      const jsonMatch = response.match(/\[[\s\S]*\]/);
      if (jsonMatch) {
        const slidesArray = JSON.parse(jsonMatch[0]);
        console.log('Successfully parsed JSON with', slidesArray.length, 'slides');

        if (Array.isArray(slidesArray) && slidesArray.length > 0) {
          const validatedSlides = slidesArray.map((slideData, index) => {
            // Ensure each slide has proper content structure
            const slide: Slide = {
              id: `slide-${Date.now()}-${index}`,
              title: cleanTextContent(slideData.title) || `Slide ${index + 1}: ${topic}`,
              content: cleanTextContent(slideData.content) || `Content for slide ${index + 1} about ${topic}`,
              type: slideData.type || (index === 0 ? 'title' : index === slidesArray.length - 1 ? 'conclusion' : 'content'),
              notes: cleanTextContent(slideData.notes) || `Presenter notes for slide ${index + 1}`,
              imageUrl: ['image', 'image-text', 'title'].includes(slideData.type) ?
              generateImageUrl(slideData.imageKeywords || extractKeywords(slideData.title || '', slideData.content || '')) :
              undefined,
              imageAlt: `${slideData.title || `Slide ${index + 1}`} - ${slideData.imageKeywords || topic}`,
              template: selectedTemplate
            };

            console.log(`Processed slide ${index + 1}:`, {
              title: slide.title.substring(0, 50),
              content: slide.content.substring(0, 50),
              type: slide.type
            });

            return slide;
          });

          // Ensure we have the requested number of slides
          if (validatedSlides.length >= requestedSlideCount) {
            return validatedSlides.slice(0, requestedSlideCount);
          } else {
            // Generate additional slides if needed
            while (validatedSlides.length < requestedSlideCount) {
              const index = validatedSlides.length;
              validatedSlides.push({
                id: `slide-${Date.now()}-${index}`,
                title: `${topic} - Part ${index + 1}`,
                content: `This section covers additional aspects of ${topic}.\n\nKey points for part ${index + 1} will be discussed here.`,
                type: index === requestedSlideCount - 1 ? 'conclusion' : 'content',
                notes: `Presenter notes for slide ${index + 1} about ${topic}`,
                imageUrl: undefined,
                imageAlt: `${topic} - Part ${index + 1}`,
                template: selectedTemplate
              });
            }
            return validatedSlides;
          }
        }
      }
    } catch (parseError) {
      console.warn('JSON parsing failed, using fallback parsing:', parseError);
    }

    // Fallback parsing - split response into chunks
    console.log('Using fallback parsing for response');

    // Split by slide indicators
    const slideDelimiters = [
    /^#{1,3}\s+.*$/gm,
    /^slide\s+\d+[:\-]?/gmi,
    /^\d+[\.)\s]+/gm,
    /^\*\*[^*]+\*\*/gm];


    let slideTexts = [response];

    // Try different delimiters to split content
    for (const delimiter of slideDelimiters) {
      const parts = response.split(delimiter).filter((part) => part.trim().length > 20);
      if (parts.length > slideTexts.length) {
        slideTexts = parts;
      }
    }

    // If no good splits found, create chunks
    if (slideTexts.length < 2) {
      const sentences = response.split(/[.!?]+/).filter((s) => s.trim().length > 10);
      const chunkSize = Math.ceil(sentences.length / requestedSlideCount);
      slideTexts = [];

      for (let i = 0; i < sentences.length; i += chunkSize) {
        slideTexts.push(sentences.slice(i, i + chunkSize).join('. ').trim());
      }
    }

    // Create slides from text chunks
    const slides: Slide[] = [];
    for (let i = 0; i < requestedSlideCount; i++) {
      const slideText = slideTexts[i] || slideTexts[slideTexts.length - 1] || '';
      const lines = slideText.split('\n').filter((line) => line.trim());

      const title = lines[0]?.replace(/^[#*\-\d\.\):\s]+/, '').trim() || `${topic} - Slide ${i + 1}`;
      const content = lines.slice(1).join('\n').trim() || `Content for slide ${i + 1} about ${topic}`;

      slides.push({
        id: `slide-${Date.now()}-${i}`,
        title: cleanTextContent(title),
        content: cleanTextContent(content),
        type: i === 0 ? 'title' : i === requestedSlideCount - 1 ? 'conclusion' : 'content',
        notes: `Presenter notes for slide ${i + 1}`,
        imageUrl: i === 0 || i % 3 === 1 ? generateImageUrl(extractKeywords(title, content)) : undefined,
        imageAlt: `${title} - ${topic}`,
        template: selectedTemplate
      });
    }

    console.log(`Fallback parsing created ${slides.length} slides`);
    return slides;
  };

  // Clean and validate text content
  const cleanTextContent = (text: any): string => {
    if (!text) return '';

    let cleanText = String(text).trim();

    // Remove JSON-like patterns
    if (cleanText.includes('{"') ||
    cleanText.includes('"title"') ||
    cleanText.includes('"content"') ||
    cleanText.includes('"imageKeywords"') ||
    cleanText.startsWith('[{') ||
    cleanText.startsWith('```json')) {
      console.log('Filtering out JSON-like content:', cleanText.substring(0, 100));
      return '';
    }

    // Remove markdown formatting
    cleanText = cleanText.
    replace(/```[^`]*```/g, '').
    replace(/`[^`]*`/g, '').
    replace(/\*\*([^*]+)\*\*/g, '$1').
    replace(/\*([^*]+)\*/g, '$1').
    replace(/#{1,6}\s+/g, '').
    trim();

    return cleanText;
  };

  // Enhanced slide generation with improved parsing
  const generateSlides = async () => {
    if (!user?.ID || !selectedTemplate) {
      toast({
        title: "Missing Information",
        description: "Please ensure you're logged in and have selected a template.",
        variant: "destructive"
      });
      return;
    }

    if (!hasAIAccess('advanced')) {
      toast({
        title: "Access Denied",
        description: "You need Tier 2 access to use the AI Slides Generator.",
        variant: "destructive"
      });
      return;
    }

    if (!topic.trim()) {
      toast({
        title: "Topic Required",
        description: "Please enter a topic for your presentation.",
        variant: "destructive"
      });
      return;
    }

    setIsGenerating(true);
    try {
      console.log('Starting slide generation for topic:', topic);

      let context = '';
      if (selectedLessonPlan) {
        const lessonPlan = lessonPlans.find((lp) => lp.id.toString() === selectedLessonPlan);
        if (lessonPlan) {
          context = `Use this lesson plan as reference:\nTitle: ${lessonPlan.title}\nObjectives: ${lessonPlan.objectives}\nContent: ${lessonPlan.content}`;
        }
      }

      const templateInfo = `Template: ${selectedTemplate.name} (${selectedTemplate.description})`;

      // Enhanced prompt for better slide generation
      const prompt = `Create exactly ${slideCount[0]} educational presentation slides on the topic: "${topic}" for ${gradeLevel} grade students in ${subject}.

${context}

Template Style: ${templateInfo}
Additional notes: ${additionalNotes}

IMPORTANT: Generate exactly ${slideCount[0]} individual slides. Each slide must be a separate JSON object with distinct content.

Return ONLY a valid JSON array containing exactly ${slideCount[0]} slide objects in this format:
[
  {
    "title": "Slide 1 Title",
    "content": "Slide 1 main content with 3-5 bullet points or paragraphs",
    "type": "title",
    "notes": "Speaker notes for slide 1",
    "imageKeywords": "keyword1, keyword2, keyword3"
  },
  {
    "title": "Slide 2 Title", 
    "content": "Slide 2 main content - different from slide 1",
    "type": "content",
    "notes": "Speaker notes for slide 2",
    "imageKeywords": "different, keywords, here"
  }
]

Make sure each slide has:
- Unique, engaging title appropriate for the grade level
- Distinct content (not duplicated from other slides)
- Appropriate slide type (title, content, image, list, quote, comparison, conclusion)
- Detailed speaker notes
- Relevant image keywords

Generate ${slideCount[0]} unique slides with varied content types and layouts.`;

      console.log('Sending prompt to AI service...');
      const response = await geminiService(prompt);

      if (!response || response.trim() === '') {
        throw new Error('Empty response from AI service');
      }

      console.log('AI response received, parsing...');

      // Use enhanced parsing
      const slides = parseAIResponse(response, slideCount[0]);

      if (!slides || slides.length === 0) {
        throw new Error('No valid slides could be generated from AI response');
      }

      console.log(`Successfully processed ${slides.length} slides`);

      // Save to database
      const presentationData = {
        teacher_id: user.ID,
        title: topic,
        description: `${subject} presentation for ${gradeLevel} grade`,
        subject: subject,
        grade_level: gradeLevel,
        slides_data: JSON.stringify(slides),
        template_theme: selectedTemplate.id,
        total_slides: slides.length,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      const { data, error } = await window.ezsite.apis.tableCreate(15255, presentationData);

      if (error) {
        console.error('Error saving presentation:', error);
        toast({
          title: "Save Warning",
          description: "Slides generated but couldn't save to database. You can still use them.",
          variant: "default"
        });
      } else {
        console.log('Presentation saved successfully:', data);
        await loadSavedPresentations();
      }

      setCurrentPresentation({
        data: { ...presentationData, id: data?.toString() || Date.now().toString() },
        slides,
        template: selectedTemplate
      });
      setCurrentSlideIndex(0);
      setCurrentStep('review');

      toast({
        title: "Beautiful Slides Generated!",
        description: `Successfully created ${slides.length} visually engaging slides for "${topic}".`
      });

      // Reset form
      setTopic('');
      setGradeLevel('');
      setSubject('');
      setSelectedLessonPlan('');
      setAdditionalNotes('');
      setSlideCount([10]);

    } catch (error) {
      console.error('Error generating slides:', error);
      toast({
        title: "Generation Failed",
        description: error instanceof Error ? error.message : "Failed to generate slides. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsGenerating(false);
    }
  };

  // Enhanced slide management functions
  const addSlide = (newSlide: Slide) => {
    if (!currentPresentation) return;

    const updatedSlides = [...currentPresentation.slides];
    updatedSlides.splice(currentSlideIndex + 1, 0, newSlide);

    setCurrentPresentation({
      ...currentPresentation,
      slides: updatedSlides
    });

    setCurrentSlideIndex(currentSlideIndex + 1);
    setShowLayoutSelector(false);

    // Auto-save
    savePresentation(updatedSlides);

    toast({
      title: "Slide Added",
      description: "New slide has been added to your presentation."
    });
  };

  const deleteSlide = (slideId: string) => {
    if (!currentPresentation || currentPresentation.slides.length <= 1) return;

    const updatedSlides = currentPresentation.slides.filter((slide) => slide.id !== slideId);

    setCurrentPresentation({
      ...currentPresentation,
      slides: updatedSlides
    });

    if (currentSlideIndex >= updatedSlides.length) {
      setCurrentSlideIndex(updatedSlides.length - 1);
    }

    // Auto-save
    savePresentation(updatedSlides);

    toast({
      title: "Slide Deleted",
      description: "Slide has been removed from your presentation."
    });
  };

  const savePresentation = async (slides: Slide[]) => {
    if (!currentPresentation) return;

    try {
      const updatedData = {
        ...currentPresentation.data,
        slides_data: JSON.stringify(slides),
        total_slides: slides.length,
        updated_at: new Date().toISOString()
      };

      await window.ezsite.apis.tableUpdate(15255, updatedData);
    } catch (error) {
      console.error('Error auto-saving presentation:', error);
    }
  };

  const exportToPDF = async () => {
    if (!currentPresentation) return;

    try {
      toast({
        title: "Exporting PDF",
        description: "Please wait while we generate your PDF..."
      });

      // Alternative: Use the existing jsPDF approach
      const { jsPDF } = await import('jspdf');
      const pdf = new jsPDF('landscape', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      for (let i = 0; i < currentPresentation.slides.length; i++) {
        const slide = currentPresentation.slides[i];
        if (i > 0) pdf.addPage();

        // Add title
        pdf.setFontSize(24);
        pdf.setFont('helvetica', 'bold');
        pdf.text(slide.title, 20, 30);

        // Add content
        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'normal');
        const lines = pdf.splitTextToSize(slide.content, pageWidth - 40);
        pdf.text(lines, 20, 50);

        // Add slide number
        pdf.setFontSize(10);
        pdf.text(`${i + 1} / ${currentPresentation.slides.length}`, pageWidth - 30, pageHeight - 10);
      }

      pdf.save(`${currentPresentation.data.title}.pdf`);

      toast({
        title: "PDF Exported!",
        description: `Presentation exported as ${currentPresentation.data.title}.pdf`
      });
    } catch (error) {
      console.error('Error exporting PDF:', error);
      toast({
        title: "Export Failed",
        description: "Failed to export presentation to PDF.",
        variant: "destructive"
      });
    }
  };

  const loadPresentation = async (presentationData: PresentationData) => {
    try {
      // Validate slides_data before parsing
      if (!presentationData.slides_data || typeof presentationData.slides_data !== 'string') {
        throw new Error('Invalid slides data format');
      }

      const slides: Slide[] = JSON.parse(presentationData.slides_data);

      // Validate each slide has required properties with enhanced content processing
      const validatedSlides = slides.map((slide, index) => ({
        id: slide.id || `slide-${Date.now()}-${index}`,
        title: cleanTextContent(slide.title) || `Slide ${index + 1}`,
        content: cleanTextContent(slide.content) || '',
        type: slide.type || 'content',
        notes: cleanTextContent(slide.notes) || '',
        imageUrl: slide.imageUrl,
        imageAlt: slide.imageAlt,
        template: slide.template
      })) as Slide[];

      const template = templates.find((t) => t.id === presentationData.template_theme) || templates[0];

      setCurrentPresentation({
        data: presentationData,
        slides: validatedSlides,
        template
      });
      setCurrentSlideIndex(0);
      setCurrentStep('review');
      setActiveTab('generator'); // Switch to generator tab to show the presentation
    } catch (error) {
      console.error('Error loading presentation:', error);
      toast({
        title: "Load Error",
        description: "Failed to load presentation data. The presentation may be corrupted.",
        variant: "destructive"
      });
    }
  };

  const updateSlide = (slideId: string, updates: Partial<Slide>) => {
    if (!currentPresentation) return;

    const updatedSlides = currentPresentation.slides.map((slide) =>
    slide.id === slideId ? { ...slide, ...updates } : slide
    );

    setCurrentPresentation({
      ...currentPresentation,
      slides: updatedSlides
    });

    // Auto-save to database
    const saveData = async () => {
      try {
        const updatedData = {
          ...currentPresentation.data,
          slides_data: JSON.stringify(updatedSlides),
          updated_at: new Date().toISOString()
        };

        await window.ezsite.apis.tableUpdate(15255, updatedData);
      } catch (error) {
        console.error('Error auto-saving slide:', error);
      }
    };

    // Debounce save
    clearTimeout((window as any).slideSaveTimeout);
    (window as any).slideSaveTimeout = setTimeout(saveData, 1000);
  };

  const handleCreateNew = () => {
    setCurrentPresentation(null);
    setCurrentStep('template');
    setActiveTab('generator');
    // Reset form
    setSelectedTemplate(undefined);
    setTopic('');
    setGradeLevel('');
    setSubject('');
    setSelectedLessonPlan('');
    setAdditionalNotes('');
    setSlideCount([10]);
  };

  // Show loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]" data-id="vu3dsefbh" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">
        <div className="text-center" data-id="rg60jb4y8" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-blue-600" data-id="kpfbjro3m" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx" />
          <p className="text-gray-600" data-id="a1fgbvbd3" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">Loading Enhanced AI Slides Generator...</p>
        </div>
      </div>);

  }

  // Show authentication required
  if (!user) {
    return (
      <Card data-id="ycpwrubpa" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">
        <CardContent className="p-12 text-center" data-id="a72lk16a1" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">
          <Presentation className="h-12 w-12 text-gray-400 mx-auto mb-4" data-id="x3w6w6ql5" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx" />
          <h3 className="text-lg font-medium text-gray-900 mb-2" data-id="nsf5go8a3" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">Authentication Required</h3>
          <p className="text-gray-600 mb-4" data-id="oxvc168n9" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">
            Please log in to access the AI Slides Generator.
          </p>
        </CardContent>
      </Card>);

  }

  // Show access denied
  if (user && !hasAIAccess('advanced')) {
    return (
      <Card data-id="rgdii9zay" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">
        <CardContent className="p-12 text-center" data-id="qas4p06xc" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">
          <Presentation className="h-12 w-12 text-gray-400 mx-auto mb-4" data-id="3h65n3agi" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx" />
          <h3 className="text-lg font-medium text-gray-900 mb-2" data-id="nqmo8uat9" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">Tier 2 Access Required</h3>
          <p className="text-gray-600 mb-4" data-id="qzufl3lz1" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">
            The AI Slides Generator requires Tier 2 access. Please contact your administrator.
          </p>
          <Badge variant="outline" className="text-orange-600 border-orange-200" data-id="9tpavrgpl" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">
            Current Access: Tier 1 (Basic)
          </Badge>
        </CardContent>
      </Card>);

  }

  // Presentation mode
  if (isPresentationMode && currentPresentation) {
    return (
      <PresentationView
        slides={currentPresentation.slides}
        template={currentPresentation.template}
        title={currentPresentation.data.title}
        onClose={() => setIsPresentationMode(false)}
        onExportPDF={exportToPDF} data-id="ztq00zkmp" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx" />);


  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="space-y-6" data-id="3m5edctat" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">

      <div className="flex items-center justify-between" data-id="uhttkrcmy" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">
        <div data-id="1bji8zg1g" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3" data-id="5nqkek80b" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">
            <Wand2 className="h-8 w-8 text-blue-600" data-id="51p977s80" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx" />
            Enhanced AI Slides Generator
          </h1>
          <p className="text-gray-600 mt-2" data-id="8wmtiqutd" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">
            Create beautiful presentations with AI assistance and professional templates
          </p>
        </div>
        <Badge className="bg-gradient-to-r from-blue-500 to-purple-600 text-white" data-id="pdzyckq0a" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">
          Template-Based • Tier 2 Feature
        </Badge>
      </div>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6" data-id="yjzb26q2j" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">
        <TabsList className="grid w-full grid-cols-2" data-id="lzr93u7tv" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">
          <TabsTrigger value="generator" className="flex items-center gap-2" data-id="rhb1mq2t7" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">
            <Wand2 className="h-4 w-4" data-id="gomte98u6" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx" />
            Generator
          </TabsTrigger>
          <TabsTrigger value="library" className="flex items-center gap-2" data-id="k6th222oa" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">
            <Library className="h-4 w-4" data-id="05f74aztq" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx" />
            My Library
          </TabsTrigger>
        </TabsList>

        <TabsContent value="generator" className="space-y-6" data-id="sl2ma220a" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">
          {/* Progress Steps */}
          <div className="flex items-center justify-center space-x-4 mb-8" data-id="rsanz20tf" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">
            {steps.map((step, index) =>
            <div key={step.id} className="flex items-center" data-id="g83yb3a54" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">
                <div className={`flex items-center space-x-2 ${
              currentStep === step.id ? 'text-blue-600' :
              steps.findIndex((s) => s.id === currentStep) > index ? 'text-green-600' : 'text-gray-400'}`
              } data-id="vwcgfoogs" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                currentStep === step.id ? 'bg-blue-100 text-blue-600' :
                steps.findIndex((s) => s.id === currentStep) > index ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'}`
                } data-id="vfu6au7dk" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">
                    {index + 1}
                  </div>
                  <div className="text-sm" data-id="0uzu5iqpf" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">
                    <div className="font-medium" data-id="049sm49zy" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">{step.title}</div>
                    <div className="text-xs opacity-75" data-id="h1nsi749y" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">{step.description}</div>
                  </div>
                </div>
                {index < steps.length - 1 &&
              <div className={`w-8 h-0.5 mx-4 ${
              steps.findIndex((s) => s.id === currentStep) > index ? 'bg-green-600' : 'bg-gray-200'}`
              } data-id="hgt6jz5fm" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx" />
              }
              </div>
            )}
          </div>

          {/* Step Content */}
          <AnimatePresence mode="wait" data-id="mvbgecjqf" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">
            {currentStep === 'template' &&
            <motion.div
              key="template"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }} data-id="uz774do1v" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">

                <Card data-id="wb2ptl2g4" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">
                  <CardHeader data-id="vzlzigntj" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">
                    <CardTitle className="flex items-center gap-2" data-id="xg3pnlf0d" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">
                      <Palette className="h-5 w-5" data-id="zy74jy91s" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx" />
                      Select Template
                    </CardTitle>
                    <CardDescription data-id="qkuf6nch1" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">
                      Choose a design template that fits your presentation style
                    </CardDescription>
                  </CardHeader>
                  <CardContent data-id="kw8o50c4q" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">
                    <TemplateSelector
                    onSelectTemplate={setSelectedTemplate}
                    selectedTemplate={selectedTemplate} data-id="dpsj8w62x" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx" />


                    <div className="flex justify-end mt-6" data-id="x1n5ykwdr" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">
                      <Button
                      onClick={() => setCurrentStep('content')}
                      disabled={!selectedTemplate}
                      className="bg-blue-600 hover:bg-blue-700" data-id="4ycw8qlm1" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">

                        Continue to Content
                        <ChevronRight className="h-4 w-4 ml-1" data-id="bd1p19rpu" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            }

            {currentStep === 'content' &&
            <motion.div
              key="content"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }} data-id="24c1h79zs" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">

                <Card data-id="ki3ghcnrl" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">
                  <CardHeader data-id="u3bsflhk8" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">
                    <CardTitle className="flex items-center gap-2" data-id="h4qgchxu3" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">
                      <FileText className="h-5 w-5" data-id="uukygnr58" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx" />
                      Content Details
                    </CardTitle>
                    <CardDescription data-id="m60vy09nb" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">
                      Provide topic and preferences for your presentation
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6" data-id="elqzbu6rj" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="j287dkw55" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">
                      <div className="space-y-4" data-id="cydp9ogmo" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">
                        <div data-id="fvm1vjnox" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">
                          <Label htmlFor="topic" data-id="q7uzsal6h" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">Presentation Topic *</Label>
                          <Input
                          id="topic"
                          value={topic}
                          onChange={(e) => setTopic(e.target.value)}
                          placeholder="e.g., Solar System, World War II, Photosynthesis"
                          className="mt-1" data-id="akrmvoqy7" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx" />

                        </div>
                        
                        <div data-id="44b1d9fzg" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">
                          <Label htmlFor="subject" data-id="ir87rjomm" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">Subject</Label>
                          <Input
                          id="subject"
                          value={subject}
                          onChange={(e) => setSubject(e.target.value)}
                          placeholder="e.g., Science, Mathematics, History, Art"
                          className="mt-1" data-id="lf485xb4l" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx" />

                        </div>
                        
                        <div data-id="8jii4m2sp" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">
                          <Label htmlFor="gradeLevel" data-id="6z5w8zywl" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">Grade Level</Label>
                          <Input
                          id="gradeLevel"
                          value={gradeLevel}
                          onChange={(e) => setGradeLevel(e.target.value)}
                          placeholder="e.g., 5th Grade, High School, College"
                          className="mt-1" data-id="hcr440zck" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx" />

                        </div>
                        
                        <div data-id="1rv60c5vt" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">
                          <Label data-id="yzitlbhoi" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">Number of Slides: {slideCount[0]}</Label>
                          <Slider
                          value={slideCount}
                          onValueChange={setSlideCount}
                          max={20}
                          min={5}
                          step={1}
                          className="mt-2" data-id="a7ddjm870" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx" />

                        </div>
                      </div>
                      
                      <div className="space-y-4" data-id="xfdphm1we" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">
                        <div data-id="dynip45md" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">
                          <Label htmlFor="lessonPlan" data-id="9ow5578le" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">Use Lesson Plan as Reference</Label>
                          <Select value={selectedLessonPlan} onValueChange={setSelectedLessonPlan} data-id="37xyikvsa" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">
                            <SelectTrigger data-id="joyd2lvau" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">
                              <SelectValue placeholder={loadingLessonPlans ? "Loading..." : "Select lesson plan (optional)"} data-id="qfzwlb6om" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx" />
                            </SelectTrigger>
                            <SelectContent data-id="j7v8gt1pj" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">
                              {lessonPlans.map((plan) =>
                            <SelectItem key={plan.id} value={plan.id.toString()} data-id="o3libeeuf" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">
                                  {plan.title} - {plan.subject}
                                </SelectItem>
                            )}
                            </SelectContent>
                          </Select>
                        </div>
                        
                        <div data-id="y45le5iyh" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">
                          <Label htmlFor="additionalNotes" data-id="68ijgzk6z" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">Additional Notes</Label>
                          <Textarea
                          id="additionalNotes"
                          value={additionalNotes}
                          onChange={(e) => setAdditionalNotes(e.target.value)}
                          placeholder="Any specific requirements, focus areas, or additional context..."
                          className="mt-1 h-32" data-id="txalipgax" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx" />

                        </div>
                      </div>
                    </div>
                    
                    <div className="flex justify-between" data-id="olxto66mr" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">
                      <Button
                      variant="outline"
                      onClick={() => setCurrentStep('template')} data-id="p4i9i1t70" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">

                        <ArrowLeft className="h-4 w-4 mr-1" data-id="lt73g7dpv" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx" />
                        Back to Template
                      </Button>
                      
                      <Button
                      onClick={generateSlides}
                      disabled={isGenerating || !topic.trim()}
                      className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700" data-id="dfvoe5rr3" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">

                        {isGenerating ?
                      <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" data-id="mh1pa169v" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx" />
                            Generating Slides...
                          </> :

                      <>
                            <Wand2 className="mr-2 h-4 w-4" data-id="38b3z96d8" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx" />
                            Generate Presentation
                          </>
                      }
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            }

            {currentStep === 'review' && currentPresentation &&
            <motion.div
              key="review"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
              className="space-y-6" data-id="6fovebu84" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">

                {/* Presentation Controls */}
                <Card data-id="rabia1vue" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">
                  <CardContent className="p-4" data-id="qeypilzov" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">
                    <div className="flex items-center justify-between" data-id="p1imbyy87" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">
                      <div data-id="kbrwkqtay" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">
                        <h2 className="text-xl font-semibold" data-id="6c70655de" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">{currentPresentation.data.title}</h2>
                        <p className="text-gray-600" data-id="sy7v4lwyo" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">{currentPresentation.data.description}</p>
                        <div className="flex items-center space-x-2 mt-1" data-id="bvunccy51" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">
                          <Badge variant="outline" data-id="n6tgxchfu" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">{currentPresentation.template.name}</Badge>
                          <Badge variant="secondary" data-id="9jbjdqq3e" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">{currentPresentation.slides.length} slides</Badge>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2" data-id="6bs5pqifu" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">
                        <Button
                        variant="outline"
                        onClick={() => setIsPresentationMode(true)} data-id="iolvr5kwx" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">

                          <Play className="h-4 w-4 mr-1" data-id="me8cqh9tn" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx" />
                          Present
                        </Button>
                        <Button
                        variant="outline"
                        onClick={exportToPDF} data-id="1nagvo2dl" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">

                          <Download className="h-4 w-4 mr-1" data-id="595vuecgk" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx" />
                          Export PDF
                        </Button>
                        <Button
                        variant="outline"
                        onClick={() => setCurrentStep('template')} data-id="rm00xxetd" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">

                          <Plus className="h-4 w-4 mr-1" data-id="gb0v6n5yd" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx" />
                          New
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Slide Navigation */}
                <div className="flex items-center space-x-4 bg-gray-50 p-4 rounded-lg" data-id="7np73leoi" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">
                  <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setCurrentSlideIndex(Math.max(0, currentSlideIndex - 1))}
                  disabled={currentSlideIndex === 0} data-id="s1o1rghpc" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">

                    <ChevronLeft className="h-4 w-4" data-id="bsk041ht9" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx" />
                  </Button>
                  
                  <span className="text-sm font-medium" data-id="zi5qej1ue" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">
                    Slide {currentSlideIndex + 1} of {currentPresentation.slides.length}
                  </span>
                  
                  <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setCurrentSlideIndex(Math.min(currentPresentation.slides.length - 1, currentSlideIndex + 1))}
                  disabled={currentSlideIndex === currentPresentation.slides.length - 1} data-id="h2yw337d9" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">

                    <ChevronRight className="h-4 w-4" data-id="ixolu7csz" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx" />
                  </Button>
                  
                  <div className="flex-1" data-id="ppnhb4r2d" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx" />
                  
                  <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowLayoutSelector(true)} data-id="qfsb5v6vf" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">

                    <Plus className="h-4 w-4 mr-1" data-id="jrapmmwuw" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx" />
                    Add Slide
                  </Button>
                  
                  <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => deleteSlide(currentPresentation.slides[currentSlideIndex].id)}
                  disabled={currentPresentation.slides.length <= 1} data-id="wzry6d79u" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">

                    <Trash2 className="h-4 w-4 mr-1" data-id="7o6nbl2xy" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx" />
                    Delete
                  </Button>
                  
                  <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsEditingSlide(isEditingSlide ? null : currentPresentation.slides[currentSlideIndex].id)} data-id="45vprrsyx" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">

                    {isEditingSlide ? <Save className="h-4 w-4" data-id="mlh2l5fo2" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx" /> : <Edit className="h-4 w-4" data-id="xxa6n5pxe" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx" />}
                    {isEditingSlide ? 'Save' : 'Edit'}
                  </Button>
                  
                  <Button
                  variant="default"
                  size="sm"
                  onClick={() => setIsPresentationMode(true)} data-id="0jkko7lnk" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">

                    <Maximize className="h-4 w-4 mr-1" data-id="scxlrush3" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx" />
                    Full Screen
                  </Button>
                </div>

                {/* Current Slide */}
                <div className="h-96" data-id="9p407thwh" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">
                  <SlideRenderer
                  slide={currentPresentation.slides[currentSlideIndex]}
                  template={currentPresentation.template}
                  isEditing={isEditingSlide === currentPresentation.slides[currentSlideIndex].id}
                  onUpdate={(updates) => updateSlide(currentPresentation.slides[currentSlideIndex].id, updates)} data-id="0ibtpasu4" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx" />

                </div>

                {/* Slide Thumbnails */}
                <div className="bg-white p-4 rounded-lg border" data-id="ggdz5eibf" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">
                  <h3 className="text-sm font-medium text-gray-700 mb-3" data-id="yyrboao41" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">All Slides</h3>
                  <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2" data-id="97nsk2qxc" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">
                    {currentPresentation.slides.map((slide, index) =>
                  <button
                    key={slide.id}
                    onClick={() => setCurrentSlideIndex(index)}
                    className={`aspect-video bg-gray-100 rounded border-2 text-xs p-2 hover:border-blue-300 transition-colors ${
                    index === currentSlideIndex ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`
                    } data-id="jhjdwdagz" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">

                        <div className="font-medium truncate" data-id="55arue9aa" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">{slide.title}</div>
                        <div className="text-gray-500 mt-1" data-id="n4dfievka" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">{index + 1}</div>
                      </button>
                  )}
                  </div>
                </div>
              </motion.div>
            }
          </AnimatePresence>

          {/* Layout Selector Modal */}
          {showLayoutSelector && selectedTemplate &&
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-id="bn5unz7ay" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">
              <Card className="w-full max-w-4xl max-h-[80vh] overflow-y-auto" data-id="jddqa53hf" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">
                <CardHeader data-id="qkxqv1imh" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">
                  <CardTitle className="flex items-center gap-2" data-id="v0m4d9pyb" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">
                    <Grid3X3 className="h-5 w-5" data-id="ur6wrd3ff" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx" />
                    Add New Slide
                  </CardTitle>
                  <CardDescription data-id="xbh63cewc" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">
                    Choose a layout for your new slide
                  </CardDescription>
                </CardHeader>
                <CardContent data-id="cel9vppoy" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">
                  <LayoutSelector
                  onSelectLayout={(layoutId, slideType) => {






















































































































                    // This will be handled by the onAddSlide callback
                  }} selectedTemplate={selectedTemplate} onAddSlide={addSlide} data-id="bc3ipfj68" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx" />


                  <div className="flex justify-end mt-6" data-id="mqmpjq9sq" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">
                    <Button variant="outline" onClick={() => setShowLayoutSelector(false)} data-id="gzrs5gtbz" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">
                      Cancel
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>}
        </TabsContent>

        <TabsContent value="library" data-id="81eae7s71" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx">
          <PresentationManager onCreateNew={handleCreateNew} onEditPresentation={loadPresentation} data-id="rlgv32vld" data-path="src/components/ai/EnhancedAISlidesGenerator.tsx" />
        </TabsContent>
      </Tabs>
    </motion.div>);};export default EnhancedAISlidesGenerator;